import React, {useState} from 'react';

import {Button, IButtonProps} from '../../components/button';
import {Card, CardContent,Grid, GridColumn,IDataInfoProps, Scroll,ITableCellProps, ITableProps, ITableRowProps, Table} from '../../components';
import {Container} from '../../components/container';
import {Dropdown} from '../../components/dropdown';
import {Filter, IFilterProps, ITextFieldProps, IToggleProps, TextField, Toggle} from '../../components/forms';

export interface ITemplateQuestionsProps {
    table: ITemplateQuestionsTableProps;
    onboardingTable: ITemplateQuestionsTableProps | undefined;
    title: string;
    filter?: IFilterProps;
    rightToolbarItems?: ITemplateQuestionsToolbar;
    addQuestion?: IButtonProps;
}

export interface ITemplateQuestionsToolbar {
    actions?: ITemplateQuestionsAction;
    search?: ITextFieldProps;
    buttons?: IButtonProps[];
}

export interface ITemplateQuestionsTableProps extends ITableProps {
    rows: ITemplateQuestionsRowProps[] | IDataInfoProps;
}

export interface ITemplateQuestionsRowProps extends ITableRowProps {
    actions?: ITemplateQuestionsAction;
    subRows?: ITemplateQuestionsRowProps[];
    toggle? : IToggleProps;
}

export interface ITemplateQuestionsAction {
    options: IButtonProps[] | undefined;
    trigger?: IButtonProps;
}

const TemplateQuestions = (props: ITemplateQuestionsProps): JSX.Element => {
    const [openActionsId, setOpenActionsId] = useState<string | number | undefined>(undefined);

    const renderToolbar = (toolbar: ITemplateQuestionsToolbar): JSX.Element => (
        <Grid noWrap={['sm']} align={['middle-xs']} justify={['end-xs']}>
            {toolbar.search &&
                (<GridColumn width={['xs-12', 'max-sm', 'lg-6']}>
                    <TextField {...toolbar.search} iconLeft="search" />
                </GridColumn>)
            }
            {toolbar.buttons && (
                <GridColumn width={['xs']}>
                    {toolbar.buttons.length === 1 ? (
                        <Button {...toolbar.buttons[0]} color="white" iconLeft="plus" />
                    ) : (
                        <Grid noWrap={['sm']} align={['middle-xs']} justify={['end-xs']}>
                            {toolbar.buttons.map((button, index) => (
                                <GridColumn key={index} width={['xs']}>
                                    <Button {...button} color="white" iconLeft="plus" />
                                </GridColumn>
                            ))}
                        </Grid>
                    )}
                </GridColumn>
            )}
            {toolbar.actions &&
                <GridColumn width={['xs']}>
                    {renderActions(toolbar.actions, 'toolbar-id')}
                </GridColumn>
            }
        </Grid>
    );

    const renderActions = (actions: ITemplateQuestionsAction, id: string | number | undefined): JSX.Element => {
        if (actions.options!.length === 1 && id === 'toolbar-id') {
            return <Button {...actions.options![0]} style="link"/>;
        }

        return (
            <Dropdown
                placement="bottom"
                trigger={{
                    children: <Button
                        text=""
                        style="link"
                        iconLeft="more"
                        hideLabel={true}
                        onClick={(event): void => {
                            if (actions.trigger?.onClick) {
                                actions.trigger.onClick(event);
                            }
                        }}
                    />,
                }}
                isOpen={typeof openActionsId !== 'undefined' ? openActionsId === id : false}
                onVisibleChange={(visible: boolean): void => {
                    if (visible) {
                        setOpenActionsId(id);
                    }
                    if (!visible && openActionsId === id) {
                        setOpenActionsId(undefined);
                    }
                }}
                triggerType="click"
            >
                <div className="text">
                    {actions.options!.map((action: IButtonProps, i: number) => (
                        <div key={'history-action-' + i}>
                            <Button {...action} onClick={(event): void => {
                                if (action.onClick) {
                                    action.onClick(event);
                                }
                                setOpenActionsId(undefined);
                            }} style="link"/>
                        </div>
                    ))}
                </div>
            </Dropdown>
        );
    };

    const getTableRow: (row: ITemplateQuestionsRowProps, depth: number) => ITableRowProps = (row: ITemplateQuestionsRowProps, depth: number) => {
        const subRows: ITemplateQuestionsRowProps[] = row.subRows ?
            row.subRows.map((subRow: ITemplateQuestionsRowProps) => (
                getTableRow(subRow, depth + 1)
            ))
            : [];

        if (props.addQuestion && depth < 1) {
            subRows.push({
                cells: [{
                    content: <Button {...props.addQuestion} color='white' iconLeft="plus" />,
                }],
            });
        }

        return {
            ...row,
            cells: row.cells.map((cell: ITableCellProps, index: number) => (
                {
                    ...cell,
                    content: (row.actions || row.toggle) ? (
                        <Grid align={['middle-xs']} gutter='none'>
                            <GridColumn width={['max-xs']}>
                                {row.toggle && index + 1 === row.cells.length ? <Toggle {...row.toggle} /> : cell.content}
                            </GridColumn>
                            {row.actions && index + 1 === row.cells.length &&
                                <GridColumn width={['min-xs']}>
                                    {renderActions(row.actions, row.id)}
                                </GridColumn>
                            }
                        </Grid>
                    ) : cell.content,
                }
            )),
            subRows: subRows.length > 0 ? subRows : undefined,
        };
    };

    const tableProps: ITableProps = {
        ...props.table,
        rows: ((props.table.rows as ITableRowProps[]).length > 0) ? (props.table.rows as ITableRowProps[]).map((row: ITemplateQuestionsRowProps, idx: number) => (
            getTableRow({...row, id: `questions-${idx}`}, 0)
        )) : props.table.rows,
    };

    const onboardingTableProps: ITableProps | undefined =
        (props.onboardingTable && (props.onboardingTable.rows as ITableRowProps[]).length > 0) ? {
            ...props.onboardingTable,
            rows: (props.onboardingTable.rows as ITableRowProps[]).map((row: ITemplateQuestionsRowProps, idx: number) => (
                getTableRow({ ...row, id: `onboarding-${idx}` }, 0)
            )),
        } : undefined;

    return (
        <Container>
            <Card>
                <CardContent>
                    <div className="text">
                        <Grid noWrap={['xl']} justify={['between-xs']} align={['middle-xs']}>
                            <GridColumn width={props.rightToolbarItems ? ['xs-12', 'lg', 'xl-6'] : ['xs-12']}>
                                <Grid noWrap={['sm']} align={['middle-xs']} justify={['between-xs', 'start-lg']}>
                                    <GridColumn width={['xs-8', 'min-sm']}>
                                        <h2>{props.title}</h2>
                                    </GridColumn>
                                    {props.filter &&
                                    <GridColumn align={['middle-xs']} width={['max-sm']}>
                                        <Filter {...props.filter} hideLabel={true} />
                                    </GridColumn>
                                    }
                                </Grid>
                            </GridColumn>
                            {props.rightToolbarItems &&
                                <GridColumn width={['xs-12', 'sm-12', 'lg-12', 'xl-6']}>
                                    {renderToolbar(props.rightToolbarItems)}
                                </GridColumn>
                            }
                        </Grid>
                        <Scroll>
                            <Table {...tableProps} />
                            {onboardingTableProps && <Table {...onboardingTableProps} />}
                        </Scroll>
                    </div>
                </CardContent>
            </Card>
        </Container>
    );
};

export default TemplateQuestions;
